import type { SimCtx } from '../context';
import { wrapCoord, wrapDelta } from '../world';
import { dhypot } from '../../core/dmath';

const MAGNET_SPEED = 14;

/** Fraction of the collector's maximum HP restored by a heart. */
export const HEART_HEAL_FRACTION = 0.15;

/** XP gems / heals / shards: magnetize toward the nearest player in pickupRadius, then collect. */
export function updatePickups(
  ctx: SimCtx,
  dt: number,
  onCollect: (player: number, kind: number, amount: number) => void,
  speedMultiplier = 1,
): void {
  const w = ctx.w;
  const pk = w.pickups;
  const high = pk.pool.high;
  for (let i = 1; i < high; i++) {
    if (!pk.pool.alive[i]) continue;
    pk.life[i] -= dt;
    if (pk.life[i] <= 0) {
      pk.pool.release(i);
      continue;
    }
    let target = pk.magnet[i];
    if (target >= 0 && !w.players[target]?.alive) pk.magnet[i] = target = -1;
    if (target < 0) {
      for (let pi = 0; pi < w.players.length; pi++) {
        const pl = w.players[pi];
        if (!pl.alive) continue;
        const r = ctx.stats[pi].pickupRadius;
        const dx = wrapDelta(pl.x - pk.x[i], w.half);
        const dy = wrapDelta(pl.y - pk.y[i], w.half);
        if (dx * dx + dy * dy <= r * r) {
          target = pi;
          pk.magnet[i] = pi;
          break;
        }
      }
    }
    if (target >= 0) {
      const pl = w.players[target];
      const dx = wrapDelta(pl.x - pk.x[i], w.half);
      const dy = wrapDelta(pl.y - pk.y[i], w.half);
      const d = dhypot(dx, dy);
      if (d < 0.6) {
        onCollect(target, pk.kind[i], pk.amount[i]);
        pk.pool.release(i);
        continue;
      }
      const sp = (MAGNET_SPEED + (20 - Math.min(20, d)) * 0.5) * speedMultiplier;
      pk.x[i] = wrapCoord(pk.x[i] + (dx / d) * sp * dt, w.half);
      pk.y[i] = wrapCoord(pk.y[i] + (dy / d) * sp * dt, w.half);
    }
  }
  if ((w.tick & 31) === 0) pk.pool.compactHigh();
}

/** Pull every pickup on the field toward its owner or the nearest living player. */
export function vacuumAll(ctx: SimCtx, player?: number): void {
  const w = ctx.w;
  const pk = w.pickups;
  for (let i = 1; i < pk.pool.high; i++) {
    if (!pk.pool.alive[i]) continue;
    let target = player ?? pk.magnet[i];
    if (target === undefined || target < 0 || !w.players[target]?.alive) {
      target = -1;
      let nearest = Infinity;
      for (const p of w.players) {
        if (!p.alive) continue;
        const dx = wrapDelta(p.x - pk.x[i], w.half);
        const dy = wrapDelta(p.y - pk.y[i], w.half);
        const d2 = dx * dx + dy * dy;
        if (d2 < nearest) {
          nearest = d2;
          target = p.index;
        }
      }
    }
    pk.magnet[i] = target;
  }
}

/** Credit remaining pickups before stage-clear pauses simulation and stage loading deletes them. */
export function collectAll(ctx: SimCtx, onCollect: (player: number, kind: number, amount: number) => void): void {
  const w = ctx.w;
  const pk = w.pickups;
  for (let i = 1; i < pk.pool.high; i++) {
    if (!pk.pool.alive[i]) continue;
    let target = pk.magnet[i];
    if (target < 0 || !w.players[target]?.alive) {
      target = -1;
      let nearest = Infinity;
      for (const p of w.players) {
        if (!p.alive) continue;
        const dx = wrapDelta(p.x - pk.x[i], w.half);
        const dy = wrapDelta(p.y - pk.y[i], w.half);
        const d2 = dx * dx + dy * dy;
        if (d2 < nearest) { nearest = d2; target = p.index; }
      }
    }
    if (target < 0) continue;
    onCollect(target, pk.kind[i], pk.amount[i]);
    pk.pool.release(i);
  }
}
